// The mapped output, browser only: each placement draws its slice into an output buffer that the pin maps into the box.
import { HEIGHT, WIDTH } from '~/lib/constants'
import { invert3, quadToQuad, rectQuad } from '~/lib/homography'
import { fitQuad, sliceColor } from '~/lib/map'
import { drawGuides } from '~/lib/map/guides'
import { buildMasks } from '~/lib/map/masks'
import { placements } from '~/lib/map/placement'
import { drawTestCard } from '~/lib/map/testCard'

const MAX_BUFFER = 4096
// The frame-space card's resolution, in frames.
const FRAME_CARD = 2
const WHITE = [255, 255, 255]

const VS = `#version 300 es
in vec2 aPos;
uniform vec2 uSize;
out vec2 vPos;
void main() {
	vPos = aPos;
	vec2 c = aPos / uSize * 2.0 - 1.0;
	gl_Position = vec4(c.x, -c.y, 0.0, 1.0);
}`

const FS_SLICE = `#version 300 es
precision highp float;
in vec2 vPos;
uniform mat3 uToFrame;
uniform vec2 uSize;
uniform sampler2D uFrame;
uniform sampler2D uMask;
uniform vec3 uTint;
out vec4 o;
void main() {
	vec3 q = uToFrame * vec3(vPos, 1.0);
	if (q.z <= 0.0) discard;
	vec2 uv = q.xy / q.z;
	if (uv.x < 0.0 || uv.y < 0.0 || uv.x > 1.0 || uv.y > 1.0) discard;
	float m = texture(uMask, vPos / uSize).a;
	o = vec4(texture(uFrame, uv).rgb * uTint * m, m);
}`

// The card over the whole output, unmasked, and the guides over everything; both straight from an output-px texture.
const FS_LAYER = `#version 300 es
precision highp float;
in vec2 vPos;
uniform vec2 uSize;
uniform sampler2D uTex;
out vec4 o;
void main() {
	o = texture(uTex, vPos / uSize);
}`

const FS_PIN = `#version 300 es
precision highp float;
in vec2 vPos;
uniform mat3 uToOutput;
uniform vec2 uOutSize;
uniform sampler2D uOut;
out vec4 o;
void main() {
	vec3 q = uToOutput * vec3(vPos, 1.0);
	vec2 p = q.xy / q.z;
	if (q.z <= 0.0 || p.x < 0.0 || p.y < 0.0 || p.x > uOutSize.x || p.y > uOutSize.y) {
		o = vec4(0.0, 0.0, 0.0, 1.0);
		return;
	}
	o = vec4(texture(uOut, vec2(p.x / uOutSize.x, 1.0 - p.y / uOutSize.y)).rgb, 1.0);
}`

const colMajor = m => [m[0], m[3], m[6], m[1], m[4], m[7], m[2], m[5], m[8]]

const program = (gl, fs) => {
	const shader = (type, src) => {
		const s = gl.createShader(type)
		gl.shaderSource(s, src)
		gl.compileShader(s)
		if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s))
		return s
	}
	const p = gl.createProgram()
	gl.attachShader(p, shader(gl.VERTEX_SHADER, VS))
	gl.attachShader(p, shader(gl.FRAGMENT_SHADER, fs))
	gl.bindAttribLocation(p, 0, 'aPos')
	gl.linkProgram(p)
	if (!gl.getProgramParameter(p, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(p))
	const loc = {}
	const n = gl.getProgramParameter(p, gl.ACTIVE_UNIFORMS)
	for (let i = 0; i < n; i++) {
		const { name } = gl.getActiveUniform(p, i)
		loc[name] = gl.getUniformLocation(p, name)
	}
	return { p, loc }
}

const texture = gl => {
	const t = gl.createTexture()
	gl.bindTexture(gl.TEXTURE_2D, t)
	gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
	gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
	gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
	gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
	return t
}

const upload = (gl, t, source) => {
	gl.bindTexture(gl.TEXTURE_2D, t)
	gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source)
}

/** A WebGL2 renderer on `canvas`, or null when the context is unavailable. */
export const createMapRenderer = canvas => {
	const gl = canvas.getContext('webgl2', { alpha: false, antialias: false, premultipliedAlpha: true })
	if (!gl) return null
	const progs = { slice: program(gl, FS_SLICE), layer: program(gl, FS_LAYER), pin: program(gl, FS_PIN) }
	const vao = gl.createVertexArray()
	gl.bindVertexArray(vao)
	const buf = gl.createBuffer()
	gl.bindBuffer(gl.ARRAY_BUFFER, buf)
	gl.enableVertexAttribArray(0)
	gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0)

	const frameTex = texture(gl)
	const cardTex = texture(gl)
	const frameCardTex = texture(gl)
	const guideTex = texture(gl)
	const outTex = texture(gl)
	const fbo = gl.createFramebuffer()
	const maskTex = new Map()
	let fboSize = ''
	let cardSize = ''
	let guidesKey = null
	let hasGuides = false
	const doc = canvas.ownerDocument
	const cardCanvas = doc.createElement('canvas')
	const guideCanvas = doc.createElement('canvas')
	upload(gl, frameCardTex, drawTestCard(doc.createElement('canvas'), FRAME_CARD * WIDTH, FRAME_CARD * HEIGHT))
	const maxBuffer = Math.min(MAX_BUFFER, gl.getParameter(gl.MAX_TEXTURE_SIZE))

	const quad = q => {
		const [a, b, c, d] = q
		gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([...a, ...b, ...c, ...a, ...c, ...d]), gl.DYNAMIC_DRAW)
		gl.drawArrays(gl.TRIANGLES, 0, 6)
	}
	const bind = (unit, tex, loc) => {
		gl.activeTexture(gl.TEXTURE0 + unit)
		gl.bindTexture(gl.TEXTURE_2D, tex)
		gl.uniform1i(loc, unit)
	}

	const setMasks = ({ slices }) => {
		for (const [id, t] of maskTex)
			if (!slices[id]) {
				gl.deleteTexture(t)
				maskTex.delete(id)
			}
		for (const [id, c] of Object.entries(slices)) {
			if (!maskTex.has(id)) maskTex.set(id, texture(gl))
			upload(gl, maskTex.get(id), c)
		}
	}

	const layer = (tex, W, H) => {
		const { p, loc } = progs.layer
		gl.useProgram(p)
		gl.uniform2f(loc.uSize, W, H)
		bind(0, tex, loc.uTex)
		quad(rectQuad(0, 0, W, H))
	}

	/** `card`: 'off'; 'masked', the card as the frame, through every slice in its colour (`tint`); 'full', over the whole output. `guides`: `drawGuides`. `pinQuad`: where the output's corners land, in box px. */
	const render = ({ data, video, card, guides, tint = true, pinQuad, box: [bw, bh] }) => {
		const { width: W, height: H } = data.output
		const k = Math.min(1, maxBuffer / Math.max(W, H))
		const fw = Math.round(W * k)
		const fh = Math.round(H * k)
		if (fboSize !== `${fw}x${fh}`) {
			fboSize = `${fw}x${fh}`
			gl.bindTexture(gl.TEXTURE_2D, outTex)
			gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, fw, fh, 0, gl.RGBA, gl.UNSIGNED_BYTE, null)
			gl.bindFramebuffer(gl.FRAMEBUFFER, fbo)
			gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, outTex, 0)
		}
		const asCard = card === 'masked'
		if (video && !asCard) upload(gl, frameTex, video)
		if (guidesKey?.[0] !== data || guidesKey[1] !== guides || guidesKey[2] !== k) {
			guidesKey = [data, guides, k]
			hasGuides = drawGuides(guideCanvas, data, guides, k)
			if (hasGuides) upload(gl, guideTex, guideCanvas)
		}

		gl.bindFramebuffer(gl.FRAMEBUFFER, fbo)
		gl.viewport(0, 0, fw, fh)
		gl.clearColor(0, 0, 0, 0)
		gl.clear(gl.COLOR_BUFFER_BIT)
		gl.enable(gl.BLEND)
		gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA)
		const full = rectQuad(0, 0, W, H)
		if (card === 'full') {
			if (cardSize !== `${W}x${H}`) {
				cardSize = `${W}x${H}`
				upload(gl, cardTex, drawTestCard(cardCanvas, fw, fh))
			}
			layer(cardTex, W, H)
		} else if (video || asCard) {
			const { p, loc } = progs.slice
			gl.useProgram(p)
			gl.uniform2f(loc.uSize, W, H)
			bind(0, asCard ? frameCardTex : frameTex, loc.uFrame)
			for (const pl of placements(data)) {
				const tex = maskTex.get(pl.key)
				const m = tex && quadToQuad(pl.quad, data.slices[pl.slice].input)
				if (!m) continue
				const [r, g, b] = asCard && tint ? sliceColor(data, pl.slice) : WHITE
				gl.uniform3f(loc.uTint, r / 255, g / 255, b / 255)
				gl.uniformMatrix3fv(loc.uToFrame, false, colMajor(m))
				bind(1, tex, loc.uMask)
				quad(pl.quad)
			}
		}
		if (hasGuides) {
			gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA)
			layer(guideTex, W, H)
		}
		gl.disable(gl.BLEND)

		gl.bindFramebuffer(gl.FRAMEBUFFER, null)
		gl.viewport(0, 0, canvas.width, canvas.height)
		gl.clearColor(0, 0, 0, 1)
		gl.clear(gl.COLOR_BUFFER_BIT)
		const toBox = quadToQuad(full, pinQuad)
		const toOutput = toBox && invert3(toBox)
		if (!toOutput) return
		const { p, loc } = progs.pin
		gl.useProgram(p)
		gl.uniform2f(loc.uSize, bw, bh)
		gl.uniform2f(loc.uOutSize, W, H)
		gl.uniformMatrix3fv(loc.uToOutput, false, colMajor(toOutput))
		bind(0, outTex, loc.uOut)
		quad(rectQuad(0, 0, bw, bh))
	}

	const destroy = () => {
		for (const t of [frameTex, cardTex, frameCardTex, guideTex, outTex, ...maskTex.values()]) gl.deleteTexture(t)
		gl.deleteFramebuffer(fbo)
		gl.deleteBuffer(buf)
		gl.deleteVertexArray(vao)
		for (const { p } of Object.values(progs)) gl.deleteProgram(p)
	}

	return { setMasks, render, destroy, gl }
}

export const mountMapRender = ({
	video,
	box,
	read,
	subscribe,
	win = box.ownerDocument.defaultView,
	fitWindow = false,
	cardWhenEmpty = false,
}) => {
	const doc = box.ownerDocument
	const canvas = doc.createElement('canvas')
	Object.assign(canvas.style, {
		position: 'absolute',
		left: '0',
		top: '0',
		width: '100%',
		height: '100%',
		display: 'none',
		pointerEvents: 'none',
	})
	canvas.dataset.mapOutput = ''
	video.after(canvas)
	let renderer = null
	try {
		renderer = createMapRenderer(canvas)
	} catch (e) {
		console.error('map renderer', e)
	}
	if (!renderer) {
		canvas.remove()
		return () => {}
	}

	const opacity = video.style.opacity
	let masksKey = null
	let dirty = true
	let frameDirty = true
	let raf = 0
	let vfc = 0

	const draw = () => {
		const { data, card = 'off', pin = true, guides = null, tint = true } = read()
		if (!data) {
			canvas.style.display = 'none'
			video.style.opacity = opacity
			return
		}
		canvas.style.display = 'block'
		video.style.opacity = '0'
		const bw = box.clientWidth
		const bh = box.clientHeight
		if (!bw || !bh) return
		const dpr = Math.min(2, win.devicePixelRatio || 1)
		const cw = Math.round(bw * dpr)
		const ch = Math.round(bh * dpr)
		if (canvas.width !== cw || canvas.height !== ch) Object.assign(canvas, { width: cw, height: ch })

		const key = [data.surfaces, data.output, Object.keys(data.slices).join()]
		if (!masksKey || key.some((v, i) => v !== masksKey[i])) {
			masksKey = key
			renderer.setMasks(buildMasks(data, doc))
		}

		let [ox, oy, vw, vh] = [0, 0, bw, bh]
		if (fitWindow && win.innerHeight) {
			const a = win.innerWidth / win.innerHeight
			vw = Math.min(bw, bh * a)
			vh = vw / a
			ox = (bw - vw) / 2
			oy = (bh - vh) / 2
		}
		const { width: W, height: H } = data.output
		const pinQuad =
			pin && data.pin
				? data.pin.map(([x, y]) => [ox + x * vw, oy + y * vh])
				: fitQuad(W, H, vw, vh).map(([x, y]) => [ox + x, oy + y])
		const hasFrame = video.readyState >= 2 && video.videoWidth > 0
		const mode = card !== 'off' ? card : cardWhenEmpty && !hasFrame ? 'masked' : 'off'
		renderer.render({ data, video: hasFrame ? video : null, card: mode, guides, tint, pinQuad, box: [bw, bh] })
	}

	const loop = () => {
		raf = win.requestAnimationFrame(loop)
		if (!video.requestVideoFrameCallback) frameDirty = true
		if (!dirty && !frameDirty) return
		dirty = false
		frameDirty = false
		draw()
	}
	const onFrame = () => {
		frameDirty = true
		vfc = video.requestVideoFrameCallback(onFrame)
	}
	if (video.requestVideoFrameCallback) vfc = video.requestVideoFrameCallback(onFrame)
	const touch = () => {
		dirty = true
	}
	const ro = new (win.ResizeObserver ?? ResizeObserver)(touch)
	ro.observe(box)
	win.addEventListener('resize', touch)
	const VIDEO_EVENTS = ['loadeddata', 'emptied', 'resize']
	for (const type of VIDEO_EVENTS) video.addEventListener(type, touch)
	const unsubscribe = subscribe(touch)
	raf = win.requestAnimationFrame(loop)

	return () => {
		win.cancelAnimationFrame(raf)
		if (vfc) video.cancelVideoFrameCallback?.(vfc)
		ro.disconnect()
		win.removeEventListener('resize', touch)
		for (const type of VIDEO_EVENTS) video.removeEventListener(type, touch)
		unsubscribe()
		renderer.destroy()
		canvas.remove()
		video.style.opacity = opacity
	}
}
